/**
 * Builds the drawer's image-led category cards from the same navigation menu
 * selected in Header -> Menu. Optional nested override blocks are matched by
 * collection handle, so override settings never control which categories exist.
 */
const GROUP_SELECTOR = '.aura-drawer__category-group';
const hydratedGroups = new WeakSet();

const normalize = (value) => (value || '').trim().toLocaleLowerCase();


/* These are optional visual overrides only. Real navigation destinations and
   row order always come from the selected Shopify Header -> Menu link list. */
const MENU_ICON_MARKUP = {
  tag: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3.4 13.4a2 2 0 0 1-.6-1.4V5a2 2 0 0 1 2-2h7a2 2 0 0 1 1.4.6l7.4 7a2 2 0 0 1 0 2.8Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><circle cx="7.5" cy="7.5" r="1.2" fill="currentColor"/></svg>',
  sparkle: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2.5 14.7 9.3 21.5 12l-6.8 2.7L12 21.5l-2.7-6.8L2.5 12l6.8-2.7L12 2.5Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>',
  shield: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2.5 20 5v6.1c0 5.3-3.5 8.6-8 10.4-4.5-1.8-8-5.1-8-10.4V5l8-2.5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="m12 7.5 1.1 2.3 2.5.4-1.8 1.8.4 2.5-2.2-1.2-2.2 1.2.4-2.5-1.8-1.8 2.5-.4L12 7.5Z" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/></svg>',
  phone: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M21 16.4v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 1.2 3.7 2 2 0 0 1 3.2 1.5h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L7.1 9.4a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.5 2.8.7a2 2 0 0 1 1.7 2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  gift: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 9h18v12H3zM2 5h20v4H2zM12 5v16M12 5H7.5a2.5 2.5 0 1 1 2.5-2.5V5Zm0 0h4.5A2.5 2.5 0 1 0 14 2.5V5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>',
  heart: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20.8 8.7c0 5.3-8.8 11-8.8 11S3.2 14 3.2 8.7A4.2 4.2 0 0 1 12 6.9a4.2 4.2 0 0 1 8.8 1.8Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>'
};

function createMenuIcon(name) {
  const markup = MENU_ICON_MARKUP[name];
  if (!markup) return null;
  const wrapper = document.createElement('span');
  wrapper.className = 'aura-drawer__menu-icon';
  const template = document.createElement('template');
  template.innerHTML = markup;
  wrapper.append(template.content.firstElementChild);
  return wrapper;
}

function applyMenuItemOverrides(drawer, nav, configRoot) {
  const overrides = new Map();
  configRoot?.querySelectorAll('[data-aura-link-override]').forEach((node) => {
    const match = normalize(node.dataset.matchTitle);
    if (match) overrides.set(match, node);
  });

  nav.querySelectorAll('[data-aura-menu-item]').forEach((item) => {
    const match = normalize(item.dataset.auraMenuTitle);
    const override = overrides.get(match);
    if (!override) return;

    const row = item.querySelector('[data-aura-menu-row]');
    if (!row) return;

    const title = row.querySelector('.aura-drawer__menu-title');
    const labelOverride = override.dataset.labelOverride?.trim();
    if (labelOverride && title) title.textContent = labelOverride;

    const destination = override.dataset.destination?.trim();
    if (destination) {
      if (row instanceof HTMLAnchorElement) {
        row.href = destination;
      } else {
        const allLink = item.querySelector('.aura-drawer__menu-child--all');
        if (allLink instanceof HTMLAnchorElement) allLink.href = destination;
      }
    }

    const iconName = normalize(override.dataset.icon);
    if (iconName && iconName !== 'none') {
      const oldIcon = row.querySelector('.aura-drawer__menu-icon');
      oldIcon?.remove();
      const icon = createMenuIcon(iconName);
      if (icon) row.insertBefore(icon, title || row.firstChild);
    }

    const badgeText = override.dataset.badge?.trim();
    row.querySelector('.aura-drawer__menu-badge')?.remove();
    if (badgeText) {
      const badge = document.createElement('span');
      badge.className = 'aura-drawer__menu-badge';
      badge.textContent = badgeText;
      const chevron = row.querySelector('.aura-drawer__menu-chevron');
      row.insertBefore(badge, chevron || null);
    }
  });
}

function arrangeDrawerNavigation(drawer) {
  if (!(drawer instanceof HTMLElement) || drawer.dataset.auraNavArranged === 'true') return;
  const nav = drawer.querySelector('[data-aura-primary-nav]');
  const configRoot = drawer.querySelector('[data-aura-config-blocks]');
  if (!nav) return;

  // Read customisation metadata before moving the visible blocks from their
  // Theme Editor host container into the visual navigation layout.
  const promo = configRoot?.querySelector('.aura-drawer-promo');
  const group = configRoot?.querySelector(GROUP_SELECTOR);

  if (promo) nav.parentNode.insertBefore(promo, nav);

  if (group) {
    const parentTitle = normalize(group.dataset.parentLinkTitle || 'categories');
    const menuItems = Array.from(nav.querySelectorAll(':scope > [data-aura-menu-item]'));
    const matchingItem = menuItems.find((item) => normalize(item.dataset.auraMenuTitle) === parentTitle);

    if (matchingItem) {
      matchingItem.replaceChildren(group);
      matchingItem.classList.add('aura-drawer__menu-item--category-group');
    } else {
      // Keep the group reachable and let its Theme Editor diagnostic explain
      // the mismatch, rather than silently dropping the configured block.
      nav.insertBefore(group, nav.firstChild);
    }
  }

  applyMenuItemOverrides(drawer, nav, configRoot);
  drawer.dataset.auraNavArranged = 'true';
}

function makeChevron() {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('class', 'aura-drawer__chevron');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  const path = document.createElementNS(ns, 'path');
  path.setAttribute('d', 'm9 5 7 7-7 7');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.6');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  svg.append(path);
  return svg;
}

function setCardImage(card, url, alt, title, imageOptions = {}) {
  const media = card.querySelector('.aura-drawer-category__media');
  if (!media) return;

  media.replaceChildren();

  if (url) {
    const img = document.createElement('img');
    img.src = url;
    img.alt = alt || title || '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.sizes = '(max-width: 400px) 30vw, 118px';

    if (imageOptions.srcset) {
      img.srcset = imageOptions.srcset;
    }

    const width = Number(imageOptions.width);
    const height = Number(imageOptions.height);
    img.width = Number.isFinite(width) && width > 0 ? width : 520;
    img.height = Number.isFinite(height) && height > 0 ? height : 520;

    media.append(img);
    return;
  }

  const placeholder = document.createElement('span');
  placeholder.className = 'aura-drawer-category__placeholder';
  placeholder.setAttribute('aria-hidden', 'true');
  placeholder.textContent = title || 'Jewellery';
  media.append(placeholder);
}

function setTextOverride(card, selector, value, hideWhenEmpty = false) {
  const node = card.querySelector(selector);
  if (!node) return;
  if (value) {
    node.textContent = value;
    if (hideWhenEmpty) node.hidden = false;
  } else if (hideWhenEmpty) {
    node.textContent = '';
    node.hidden = true;
  }
}

function createCard(sourceLink, mode, override) {
  const title = sourceLink.dataset.title?.trim() || 'Shop collection';
  const href = sourceLink.dataset.url || '#';
  const handle = sourceLink.dataset.collectionHandle?.trim() || '';
  const collectionImage = sourceLink.dataset.collectionImage?.trim() || '';
  const productImage = sourceLink.dataset.productImage?.trim() || '';

  const card = document.createElement('a');
  card.className = 'aura-drawer-category';
  card.href = href;
  card.dataset.collectionHandle = handle;
  if (sourceLink.dataset.current === 'true') {
    card.classList.add('is-current');
    card.setAttribute('aria-current', 'page');
  } else if (sourceLink.dataset.active === 'true') {
    card.setAttribute('aria-current', 'location');
  }

  const media = document.createElement('span');
  media.className = 'aura-drawer-category__media';
  card.append(media);

  const copy = document.createElement('span');
  copy.className = 'aura-drawer-category__copy';
  const titleNode = document.createElement('span');
  titleNode.className = 'aura-drawer-category__title';
  titleNode.textContent = title;
  const subtitleNode = document.createElement('span');
  subtitleNode.className = 'aura-drawer-category__subtitle';
  subtitleNode.hidden = true;
  subtitleNode.dataset.auraSubtitle = '';
  copy.append(titleNode, subtitleNode);
  card.append(copy, makeChevron());

  if (override) {
    const overrideTitle = override.dataset.title?.trim();
    const overrideSubtitle = override.dataset.subtitle?.trim();
    const overrideLink = override.dataset.link?.trim();
    if (overrideTitle) titleNode.textContent = overrideTitle;
    setTextOverride(card, '.aura-drawer-category__subtitle', overrideSubtitle, true);
    if (overrideLink) card.href = overrideLink;
  }

  // Always resolve a default image. Even when the group is configured as
  // "custom_only", a category without its own override should fall back to
  // the collection image, then the first product image.
  const defaultImage = mode === 'collection_only'
    ? collectionImage
    : (collectionImage || productImage);

  const customImage = override?.dataset.imageSrc?.trim() || '';
  const canUseCustom = mode === 'custom_preferred' || mode === 'custom_only';
  const useCustomImage = canUseCustom && Boolean(customImage);
  const finalImage = useCustomImage ? customImage : defaultImage;

  const alt = useCustomImage
    ? (override?.dataset.imageAlt?.trim() || titleNode.textContent.trim())
    : titleNode.textContent.trim();

  const imageOptions = useCustomImage
    ? {
        srcset: override?.dataset.imageSrcset?.trim() || '',
        width: override?.dataset.imageWidth,
        height: override?.dataset.imageHeight
      }
    : {};

  setCardImage(card, finalImage, alt, titleNode.textContent.trim(), imageOptions);
  return card;
}

function applyOverridesToRenderedCards(group, overrides, mode, sourceParent) {
  const sourceByHandle = new Map();

  sourceParent?.querySelectorAll(':scope > [data-aura-nav-child]').forEach((sourceLink) => {
    const handle = normalize(sourceLink.dataset.collectionHandle);
    if (handle) sourceByHandle.set(handle, sourceLink);
  });

  group.querySelectorAll('.aura-drawer__category-list .aura-drawer-category[data-collection-handle], [data-aura-category-list] .aura-drawer-category[data-collection-handle]').forEach((card) => {
    const handle = normalize(card.dataset.collectionHandle);
    const override = overrides.get(handle);
    const sourceLink = sourceByHandle.get(handle);
    const titleNode = card.querySelector('.aura-drawer-category__title');

    if (!titleNode) return;

    const originalTitle = titleNode.textContent.trim();
    const overrideTitle = override?.dataset.title?.trim() || '';
    if (overrideTitle) titleNode.textContent = overrideTitle;

    setTextOverride(
      card,
      '.aura-drawer-category__subtitle',
      override?.dataset.subtitle?.trim() || '',
      true
    );

    const customLink = override?.dataset.link?.trim() || '';
    if (customLink) {
      card.href = customLink;
    } else if (card.dataset.defaultHref) {
      card.href = card.dataset.defaultHref;
    }

    const collectionImage = sourceLink?.dataset.collectionImage?.trim() || '';
    const productImage = sourceLink?.dataset.productImage?.trim() || '';
    const existingImage = card.querySelector('.aura-drawer-category__media img');
    const existingDefault = card.dataset.defaultImageSrc || existingImage?.getAttribute('src') || '';
    const defaultImage = mode === 'collection_only'
      ? (collectionImage || existingDefault)
      : (collectionImage || productImage || existingDefault);

    const customImage = override?.dataset.imageSrc?.trim() || '';
    const canUseCustom = mode === 'custom_preferred' || mode === 'custom_only';
    const useCustomImage = canUseCustom && Boolean(customImage);
    const finalImage = useCustomImage ? customImage : defaultImage;
    const alt = useCustomImage
      ? (override?.dataset.imageAlt?.trim() || titleNode.textContent.trim())
      : (card.dataset.defaultImageAlt || originalTitle || titleNode.textContent.trim());

    const imageOptions = useCustomImage
      ? {
          srcset: override?.dataset.imageSrcset?.trim() || '',
          width: override?.dataset.imageWidth,
          height: override?.dataset.imageHeight
        }
      : {
          srcset: existingImage?.getAttribute('srcset') || '',
          width: existingImage?.getAttribute('width') || '',
          height: existingImage?.getAttribute('height') || ''
        };

    setCardImage(card, finalImage, alt, titleNode.textContent.trim(), imageOptions);
  });
}

function hydrateGroup(group) {
  if (!(group instanceof HTMLElement) || hydratedGroups.has(group)) return;
  hydratedGroups.add(group);

  const drawer = group.closest('.aura-mobile-drawer') || document;
  const sourceRoot = drawer.querySelector('[data-aura-nav-source]') || document.querySelector('[data-aura-nav-source]');
  const parentTitle = normalize(group.dataset.parentLinkTitle || 'categories');
  const sourceParent = Array.from(sourceRoot?.querySelectorAll('[data-aura-nav-parent]') || [])
    .find((item) => normalize(item.dataset.parentTitle) === parentTitle);

  // Support both the data attributes used by the sync script and the class
  // names in drawer-category-group.liquid.
  const list = group.querySelector('[data-aura-category-list], .aura-drawer__category-list');
  const summary = group.querySelector(':scope > summary');
  const configNote = group.querySelector('[data-aura-category-config-note], .aura-drawer__category-config-note');
  const mode = group.dataset.imageMode || 'custom_preferred';
  const allTitle = normalize(group.dataset.allLinkTitle || 'all categories');
  // The setting's schema defaults to true; retain that default when the markup
  // doesn't expose data-use-all-link-as-cta.
  const useAllAsCta = group.dataset.useAllLinkAsCta !== 'false';

  // This implementation intentionally has no "View all categories" CTA.
  group.querySelectorAll('[data-aura-view-all], .aura-drawer__view-all').forEach((node) => node.remove());

  const overrides = new Map();
  group.querySelectorAll('.aura-drawer-category-override[data-collection-handle]').forEach((override) => {
    const handle = normalize(override.dataset.collectionHandle);
    if (handle) overrides.set(handle, override);
  });

  if (sourceParent && list) {
    // Rebuild cards only when the source navigation is available. Otherwise,
    // keep the Liquid-rendered cards and apply overrides in place below.
    list.replaceChildren();
    const children = Array.from(sourceParent.querySelectorAll(':scope > [data-aura-nav-child]'));
    children.forEach((sourceLink) => {
      const titleKey = normalize(sourceLink.dataset.title);
      if (useAllAsCta && titleKey === allTitle) return;

      const handle = normalize(sourceLink.dataset.collectionHandle);
      const override = handle ? overrides.get(handle) : null;
      list.append(createCard(sourceLink, mode, override));
    });
    if (configNote) configNote.hidden = true;
  } else {
    // If the source-navigation markup is unavailable, preserve the Liquid
    // rendered cards and apply available overrides without deleting the list.
    if (list) applyOverridesToRenderedCards(group, overrides, mode, sourceParent);
    if (configNote) configNote.hidden = false;
  }

  const syncExpandedState = () => summary?.setAttribute('aria-expanded', group.open ? 'true' : 'false');
  syncExpandedState();
  group.addEventListener('toggle', syncExpandedState);
}

function hydrate(root = document) {
  const drawers = new Set();
  if (root instanceof Element) {
    if (root.matches('.aura-mobile-drawer')) drawers.add(root);
    const closestDrawer = root.closest('.aura-mobile-drawer');
    if (closestDrawer) drawers.add(closestDrawer);
  }
  root.querySelectorAll?.('.aura-mobile-drawer').forEach((drawer) => drawers.add(drawer));
  drawers.forEach(arrangeDrawerNavigation);

  if (root instanceof Element && root.matches(GROUP_SELECTOR)) hydrateGroup(root);
  root.querySelectorAll?.(GROUP_SELECTOR).forEach(hydrateGroup);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => hydrate(), { once: true });
} else {
  hydrate();
}

document.addEventListener('shopify:section:load', (event) => hydrate(event.target));
const observationRoot = document.body || document.documentElement;
if (observationRoot) {
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof Element) hydrate(node);
      }
    }
  });
  observer.observe(observationRoot, { childList: true, subtree: true });
}